// HEAL-46b §9 — push-уведомления: разрешение, токен устройства, переход по тапу.
// Source of truth: docs/specs/heal-46-batch-upload/stage-2-notifications.md
//
// Шлюз — Firebase на обеих ОС (D-9, D-26). Отсюда плагин @capacitor-firebase/messaging,
// а не штатный @capacitor/push-notifications: последний на iOS отдаёт токен APNs,
// а FCM адресует только своим registration token. С ним пришлось бы держать два
// канала — Android через Firebase, iOS напрямую в APNs с ключом на нашем сервере.
//
// Всё в этом модуле — no-op на web (D-9: push в браузере не делаем).

import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Preferences } from '@capacitor/preferences';
import { api } from '@/api/client';
import { getToken as getAuthToken } from './auth-storage';
import { isNative, platform } from './platform';
import { queryClient } from './queryClient';
import { queryKeys } from '@/api/queryKeys';
import { targetPathFromPayload } from './pushTarget';
import { router } from './router';

const TOKEN_KEY = 'healthlab.push.token';
const ASKED_AT_KEY = 'healthlab.push.asked_at';

/**
 * Пауза между своими вопросами «сообщить, когда будет готово?» (D-30).
 *
 * Своё окно показывать можно сколько угодно раз, системное — ровно один, и то
 * пока человек не ответил. Неделя — чтобы отказ не превращался в еженедельное
 * приставание, но и не закрывал дорогу навсегда.
 */
const ASK_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

export type PushPermission = 'granted' | 'denied' | 'prompt';

export function pushSupported(): boolean {
  return isNative();
}

export async function pushPermission(): Promise<PushPermission> {
  if (!pushSupported()) return 'denied';
  try {
    const { receive } = await FirebaseMessaging.checkPermissions();
    if (receive === 'granted') return 'granted';
    if (receive === 'denied') return 'denied';
    return 'prompt';
  } catch {
    // Плагин недоступен (старый бинарник без нативной части) — ведём себя как
    // при отказе: приложение обязано работать без push (I-15).
    return 'denied';
  }
}

/** Показывать ли своё окно с предложением. */
export async function mayAskForPush(): Promise<boolean> {
  if (!pushSupported()) return false;
  if ((await pushPermission()) !== 'prompt') return false;

  const { value } = await Preferences.get({ key: ASKED_AT_KEY });
  if (!value) return true;

  const askedAt = Number(value);
  return !Number.isFinite(askedAt) || Date.now() - askedAt > ASK_COOLDOWN_MS;
}

export async function rememberAsked(): Promise<void> {
  await Preferences.set({ key: ASKED_AT_KEY, value: String(Date.now()) });
}

/**
 * Системный запрос разрешения и регистрация токена.
 *
 * Зовётся ТОЛЬКО по явному нажатию «Сообщить» в своём окне (D-30): системное
 * окно показывается один раз за установку, и потратить его на автоматический
 * запрос при запуске — самый дешёвый способ получить отказ навсегда.
 */
export async function enablePush(): Promise<boolean> {
  if (!pushSupported()) return false;

  await rememberAsked();

  try {
    const { receive } = await FirebaseMessaging.requestPermissions();
    if (receive !== 'granted') return false;
  } catch {
    return false;
  }

  await syncPushToken();
  return true;
}

/**
 * Отдать серверу текущий токен устройства.
 *
 * Зовётся при каждом запуске приложения и при обновлении токена: FCM меняет его
 * сам (переустановка, восстановление из резервной копии), и без повторной
 * регистрации уведомления тихо перестали бы приходить.
 *
 * Молча выходит, если человек не авторизован: эндпоинт под Sanctum, а токен
 * устройства без аккаунта адресовать некому.
 */
export async function syncPushToken(): Promise<void> {
  if (!pushSupported()) return;
  if ((await pushPermission()) !== 'granted') return;
  if (!(await getAuthToken())) return;

  try {
    const { token } = await FirebaseMessaging.getToken();
    if (!token) return;

    await api.post('/push/devices', { token, platform: platform() });
    await Preferences.set({ key: TOKEN_KEY, value: token });
  } catch {
    // Служебная операция: человеку сообщать нечего, действий от него она не
    // требует. Не получилось сейчас — получится при следующем запуске.
  }
}

/** Токен, который надо отозвать при выходе из аккаунта (§8.3). */
export async function currentPushToken(): Promise<string | null> {
  if (!pushSupported()) return null;
  const { value } = await Preferences.get({ key: TOKEN_KEY });
  return value ?? null;
}

export async function forgetPushToken(): Promise<void> {
  await Preferences.remove({ key: TOKEN_KEY });
}

/** Отписка с этого устройства (D-32): сервер забывает токен, локально — тоже. */
export async function disablePush(): Promise<void> {
  const token = await currentPushToken();
  if (token) {
    try {
      await api.delete('/push/devices', { data: { token } });
    } catch {
      // Ниже всё равно забываем локально: повторная регистрация произойдёт
      // только по явному включению.
    }
  }
  await forgetPushToken();
}

/**
 * Слушатели: обновление токена и тап по уведомлению.
 *
 * Ставятся один раз при старте приложения, до авторизации: тап может прийти на
 * холодный старт, и потерять его нельзя.
 */
export function initPush(): void {
  if (!pushSupported()) return;

  void FirebaseMessaging.addListener('tokenReceived', () => {
    void syncPushToken();
  });

  void FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
    // HEAL-76: уведомление уже в ленте, и счётчик на колокольчике устарел ровно
    // в этот момент. Сбрасываем профиль — в нём живёт число непрочитанных (D-7).
    //
    // Ленту сбрасываем вместе с ним, а не только счётчик: иначе бейдж вырастет,
    // а список останется прошлым — это и выглядит как «цифра опережает
    // содержимое».
    void queryClient.invalidateQueries({ queryKey: queryKeys.profileMe });
    void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });

    const path = targetPathFromPayload(event.notification?.data);
    if (path) router.push(path);
  });
}
